import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, ButtonGroup, Spinner, Alert, Badge } from 'react-bootstrap';
import authConfig from '../../authConfig';
import SecureFileLink from '../meeting/SecureFileLink';
import { FaFileAlt, FaFilePdf, FaFileWord, FaFileExcel, FaFilePowerpoint, FaFileImage, FaFile } from 'react-icons/fa';

const MemberDocumentLibraryList = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('All');

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(`${authConfig.api.urls.getMemberLibraryDocumentListForMember}`, {
                    credentials: 'include'
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (!cancelled) setItems(Array.isArray(data) ? data : []);
            } catch (err) {
                if (!cancelled) setError('Failed to load documents');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, []);

    const categories = ['All', ...Array.from(new Set(items.map(i => i.category || 'Uncategorised'))).sort()];

    // Sort items by category then title for consistent display
    const sortedItems = items.slice().sort((a, b) => {
        const ca = (a.category || 'Uncategorised').toString().toLowerCase();
        const cb = (b.category || 'Uncategorised').toString().toLowerCase();
        if (ca < cb) return -1;
        if (ca > cb) return 1;
        const ta = (a.title || '').toString().toLowerCase();
        const tb = (b.title || '').toString().toLowerCase();
        if (ta < tb) return -1;
        if (ta > tb) return 1;
        return 0;
    });

    const filtered = sortedItems.filter(i => 
        selectedCategory === 'All' ? true : (i.category || 'Uncategorised') === selectedCategory
    );

    const getFileExtension = (filename) => {
        if (!filename) return '';
        const parts = filename.split('.');
        return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
    };

    const getFileIcon = (filename) => {
        const ext = getFileExtension(filename);
        switch (ext) {
            case 'pdf':
                return <FaFilePdf className="text-danger" />;
            case 'doc':
            case 'docx':
                return <FaFileWord className="text-primary" />;
            case 'xls':
            case 'xlsx':
                return <FaFileExcel className="text-success" />;
            case 'ppt':
            case 'pptx':
                return <FaFilePowerpoint className="text-warning" />;
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'bmp':
                return <FaFileImage className="text-info" />;
            case 'txt':
                return <FaFileAlt />;
            default:
                return <FaFile />;
        }
    };

    const snippet = (text = '', len = 200) => {
        const t = (text || '').trim();
        if (t.length <= len) return t;
        return t.slice(0, len).trimEnd() + '...';
    };

    if (loading) return (
        <Container>
            <div className="text-center py-5">
                <Spinner animation="border" />
            </div>
        </Container>
    );

    if (error) return (
        <Container>
            <Alert variant="danger">{error}</Alert>
        </Container>
    );

    return (
        <Container fluid>
            <Row className="mb-3 align-items-center">
                <Col><h5>Member Document Library</h5></Col>
                <Col className="text-end">
                    <ButtonGroup aria-label="categories">
                        {categories.map(c => (
                            <Button 
                                key={c} 
                                variant={c === selectedCategory ? 'primary' : 'outline-secondary'} 
                                size="sm" 
                                onClick={() => setSelectedCategory(c)}
                            >
                                {c}
                            </Button>
                        ))}
                    </ButtonGroup>
                </Col>
            </Row>

            {filtered.length === 0 && (
                <Row>
                    <Col>
                        <Alert variant="info">
                            No documents available for the selected category.
                        </Alert>
                    </Col>
                </Row>
            )}

            <Row xs={1} sm={1} md={2} lg={3} xxl={3} className="g-3">
                {filtered.map(item => {
                    const fileUrl = `${authConfig.api.urls.getApiUrl}api/memberlibrarydocument/formember/file/${encodeURIComponent(item.id)}/${encodeURIComponent(item.fileName || 'document')}`;
                    
                    return (
                        <Col key={item.id}>
                            <Card className="h-100">
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <Badge bg="secondary">{item.category || 'Uncategorised'}</Badge>
                                        {/*<div className="fs-4">
                                            {getFileIcon(item.fileName)}
                                        </div>*/}
                                    </div>

                                    <Card.Title className="mb-2">{item.title}</Card.Title>

                                    {item.description && (
                                        <Card.Text className="flex-grow-1 text-muted">
                                            {snippet(item.description)}
                                        </Card.Text>
                                    )}

                                    {item.fileName && (
                                        <div className="mb-2 text-muted small">
                                            <strong>File:</strong> {item.fileName}
                                        </div>
                                    )}

                                    <div className="mt-auto pt-3">
                                        <SecureFileLink
                                            url={fileUrl}
                                            filename={item.fileName || 'document'}
                                            type="button"
                                            variant="outline-primary"
                                            allowdownload={true}
                                        >
                                            View Document
                                        </SecureFileLink>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>
        </Container>
    );
};

export default MemberDocumentLibraryList;
